/**
 * Prefixes a cart or product API path with the storefront's current root.
 * @param {string} path - Path relative to the root, with no leading slash — the root carries one.
 * @returns {string}
 */
function marketUrl(path) {
  const routes = window.Shopify && window.Shopify.routes;
  const root = routes && routes.root ? routes.root : '/';
  return root + path;
}

let marketCurrencyRepair = null;
let marketCurrencyAttempts = 0;

/**
 * Re-asserts the market's country when the cart has drifted to another
 * currency, so a market subfolder prices consistently end to end.
 *
 * Reads the cart_currency cookie rather than the Cart API: it is the value
 * that drifts, and checking it costs no request on the pages where nothing is
 * wrong. Posting the country through the localization form is the same
 * mechanism a country selector uses, and unlike a cart write it re-prices a
 * cart that already holds items.
 *
 * The cookie is re-read on every call and only an in-flight repair is shared.
 * Caching the "nothing to do" answer would pin the first reading for the life
 * of the page, and the currency can drift after load. Callers must await this
 * immediately before writing to the cart; it resolves either way, since a
 * failed repair must not block adding to cart.
 * @returns {Promise<void>}
 */
function ensureMarketCurrency() {
  if (marketCurrencyRepair) return marketCurrencyRepair;

  const root = document.documentElement;
  const country = root.dataset.marketCountry;
  const expected = root.dataset.marketCurrency;

  if (!country || !expected) {
    console.warn('Market currency check skipped: market data attributes missing');
    return Promise.resolve();
  }

  const match = document.cookie.match(/(?:^|;\s*)cart_currency=([A-Z]{3})/);
  // No cookie means no cart currency has been resolved yet, which the next
  // cart write resolves on its own.
  if (!match || match[1] === expected) return Promise.resolve();

  if (marketCurrencyAttempts >= 3) {
    console.warn('Market currency still ' + match[1] + ', expected ' + expected + '; giving up after ' + marketCurrencyAttempts + ' attempts');
    return Promise.resolve();
  }
  marketCurrencyAttempts++;

  console.warn('Cart is in ' + match[1] + ' on a ' + expected + ' market; re-asserting ' + country);

  const body = new URLSearchParams();
  body.set('_method', 'put');
  body.set('country_code', country);
  body.set('return_to', marketUrl(''));

  marketCurrencyRepair = fetch(marketUrl('localization'), {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  })
    .catch(error => {
      console.warn('Market currency repair failed:', error);
    })
    .then(() => {
      marketCurrencyRepair = null;
    });

  return marketCurrencyRepair;
}

/**
 * Formats a Cart API amount for display, in the currency that payload reports.
 * Both arguments must come from the same cart response: taking the currency from
 * anywhere else lets a mismatched amount render under a plausible label.
 * @param {number} cents - Amount in minor units (`price`, `total_price`, ...).
 * @param {string} currency - ISO code from the same response (`cart.currency`).
 * @returns {string} e.g. 'DKK 59,95'.
 */
function formatCartMoney(cents, currency) {
  const locale = document.documentElement.lang || undefined;
  const amount = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format((Number(cents) || 0) / 100);

  return currency ? currency + ' ' + amount : amount;
}

// allow to show/hide products according with the collection id on section collection-slider-with-filters.liquid
function filterSlider(products, collectionId, splideObject, sectionId) {
  //remove all
  while (splideObject.length) splideObject.remove(splideObject.length - 1);
  // display collection selected
  let productsToShow = products.find(product => product.collection_id == collectionId);

  if (productsToShow && productsToShow.products.length) {
    productsToShow.products.forEach(elem => {
      splideObject.add('<li class="splide__slide">' + elem.outerHTML + '</li>');
    });
  }

  splideObject.refresh();

  // highlight the filter selected
  let allFilters = document.querySelectorAll(`.filter-label-${sectionId}`);

  allFilters.forEach(elem => {
    let temp = elem.getAttribute('collection-id');

    if (temp === collectionId) {
      elem.style.fontWeight = '600';
    } else {
      elem.style.fontWeight = 'normal';
    }
  });

  // check the number of products if <= 4 hide next button
  let nextButton = document.querySelector(`#splide-${sectionId} .splide__arrow--next`);
  let prevButton = document.querySelector(`#splide-${sectionId} .splide__arrow--prev`);
  if (splideObject.length <= 4) {
    if (nextButton) nextButton.style.visibility = 'hidden';
    if (prevButton) prevButton.style.visibility = 'hidden';
  } else {
    if (nextButton) nextButton.style.visibility = 'visible';
  }
}

// reorder slides by collection
function reorderSlides(slides) {
  let results = [];
  slides.forEach(elem => {
    let collectionId = elem.getAttribute('collection-id');
    if (!results.find(collection => collection.collection_id == collectionId)) {
      results.push({ collection_id: collectionId, products: [] });
    }
  });
  slides.forEach(elem => {
    let collectionId = elem.getAttribute('collection-id');
    let collectionIndex = results.findIndex(collection => collection.collection_id == collectionId);
    results[collectionIndex].products.push(elem.querySelector('.product-item'));
  });

  return results;
}

function IsJsonString(str) {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
}

function openCart() {

  const eventName = 'view_cart';
  getCartForDataLayer(eventName);

  let cartButton = document.querySelector('.product-detail--button');
  if (cartButton) cartButton.style.zIndex = 0;

  // remove scrollbars from body
  document.body.classList.add('body-no-scroll');

  // show drawer and overlay
  document.querySelector('.cart-drawer').classList.add('cart-drawer--show');
  document.querySelector('.div-overlay').classList.add('div-overlay--show');

  getCartInfo();
}

function getCartForDataLayer(eventName) {
  fetch(marketUrl('cart.js'), {
    method: 'GET',
  })
    .then(response => response.json())
    .then(data => {

      const cartValue = data.total_price / 100;
      const items = []
      let index = 0;

      data.items.forEach(item => {
        index++;
        items.push({
          id: item.id,
          title: item.title,
          vendor: item.vendor,
          index: index,
          type: item.product_type,
          price: item.price / 100,
          quantity: item.quantity,
        })
      })
      cartDataLayerPush(items, eventName, cartValue);
  });
}

function getCartTotal() {
  fetch(marketUrl('cart.js'), {
    method: 'GET',
  })
    .then(response => {
      response
        .json()
        .then(result => {
          document.getElementById('cart-drawer__subtitle').style.display = '';
          document.getElementById('cart-drawer__total').style.display = '';
          document.getElementById('cart-gift-wrapping').style.display = '';
          document.getElementById('cart-drawer-total__price').innerHTML = formatCartMoney(result.total_price, result.currency);

          if (result.item_count == 0 || (result.item_count == 1 && result.items[0].properties['_isGift'])) {
            document.getElementById('cart-drawer__subtitle').style.display = 'none';
            document.getElementById('cart-drawer__empty').style.display = '';
            document.getElementById('cart-drawer__total').style.display = 'none';
            document.getElementById('cart-gift-wrapping').style.display = 'none';
          }

          if (result.item_count == 1 && result.items[0].properties['_isGift']) {
            removeGiftProduct(result.items[0].id);
          }

          let temp = result.items.find(elem => elem.properties['_isGift']);
          let haveGift = temp ? -1 : 0;

          document.getElementById('cart-count').innerHTML = result.item_count + haveGift;
        })
        .catch(error => {
          console.log('catch', error);
        });
    })
    .catch(error => {
      console.log('catch', error);
    });
}

/**
 * Annotates each cart line with the compare-at price of its own variant.
 * @param {object} cart - Cart API payload; mutated in place and returned.
 * @returns {object}
 */
function addCompareAtPrice(cart) {
  cart.items.forEach(function(item) {
    $.ajax({
      url: marketUrl('products/' + item.handle + '.js'),
      dataType: 'json',
      async: false,
      success: function(product) {
        // Match first, then assign once. Assigning inside the loop let every
        // non-matching variant reset the value the matching one had just set.
        const variant = product.variants.find(function(one) {
          return one.id == item.variant_id;
        });
        item['compare_at_price'] = variant && variant.compare_at_price != null ? variant.compare_at_price : 0;
      },
    });
  });

  return cart;
}

function getCartInfo() {
  let customTags = ['<%', '%>'];
  Mustache.tags = customTags;
  let cartDrawerContent = document.querySelector('.cart-drawer__content');
  let templateCart = document.querySelector('#cartTemplate').innerHTML;
  document.getElementById('cart-drawer__spinner').style.display = '';
  let temp = null;

  let products = {
    products: [],
  };

  fetch(marketUrl('cart.js'), {
    method: 'GET',
  })
    .then(response => {
      response
        .json()
        .then(result => {
          let checkGiftWrap = false;
          const resultWithCompareAtPrice = addCompareAtPrice(result);

          if (result.items.length) {
            resultWithCompareAtPrice.items.forEach(oneProduct => {
              if (oneProduct.properties['_isGift']) {
                checkGiftWrap = true;
              } else {
                temp = {
                  image: oneProduct.properties['_image'] ? oneProduct.properties['_image'] : oneProduct.image,
                  title: oneProduct.product_title,
                  price: formatCartMoney(oneProduct.price, result.currency),
                  compareAtPrice: formatCartMoney(oneProduct.compare_at_price, result.currency),
                  // Raw minor units for the quantity handlers; the display strings above
                  // are not parseable back into a number.
                  priceCents: oneProduct.price,
                  compareAtPriceCents: oneProduct.compare_at_price,
                  productId: oneProduct.id,
                  vendor: oneProduct.vendor,
                  quantity: oneProduct.quantity,
                  discount: formatCartMoney(oneProduct.compare_at_price, result.currency),
                  hasDiscount: oneProduct.compare_at_price != oneProduct.price ? 'has-discount' : '',
                };
                products.products.push(temp);
              }
            });

            temp = Mustache.render(templateCart, products, {}, customTags);

            cartDrawerContent.innerHTML = temp;

            document.getElementById('cart-drawer-total__price').innerHTML = formatCartMoney(result.total_price, result.currency);

            document.getElementById('check-gift').checked = checkGiftWrap;
          }

          let tempItemCount = result.item_count;
          let tempGiftCount = 0;
          if (result.item_count == 1 && checkGiftWrap) {
            removeGiftProduct(result.items[0].id);
            tempItemCount = 0;
            tempGiftCount = -1;
          }
          document.getElementById('cart-count').innerHTML = result.item_count + tempGiftCount;

          if (tempItemCount == 0) {
            document.getElementById('cart-drawer__subtitle').style.display = 'none';
            document.getElementById('cart-drawer__empty').style.display = '';
            document.getElementById('cart-drawer__total').style.display = 'none';
            temp = document.getElementById('cart-gift-wrapping');
            if (temp) temp.style.display = 'none';
          } else {
            document.getElementById('cart-drawer__subtitle').style.display = '';
            document.getElementById('cart-drawer__empty').style.display = 'none';
            document.getElementById('cart-drawer__total').style.display = '';
            temp = document.getElementById('cart-gift-wrapping');
            if (temp) temp.style.display = '';
          }

          setTimeout(() => {
            document.getElementById('cart-drawer__spinner').style.display = 'none';
          }, 300);
        })
        .catch(error => {
          console.log('catch', error);
        });
    })
    .catch(error => {
      console.log('catch', error);
    });
}

function addToCart(variant_id, item_name, affiliation, compare_at, item_brand, item_category, item_variant, price, isGift, image) {
  const addData = {
    id: variant_id,
    product: {
      id: variant_id,
      title: item_name,
      vendor: affiliation,
      discount: (compare_at - price) / 100,
      designer: item_brand,
      type: item_category,
      color: item_variant,
      price: price / 100,
    },
    properties: {
      _isGift: isGift,
      _image: image,
    },
  };

  // A cart's currency is fixed once it exists, so the market has to be correct
  // before the first line is added — not repaired afterwards.
  ensureMarketCurrency()
    .then(() =>
      fetch(marketUrl('cart/add.js'), {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(addData),
      })
    )
    .then(response => {
      response
        .json()
        .then(result => {
          if (response.status === 200) {
            addToCartDataLayerPush(addData.product);
            closeLeftDrawer();
            openCart();
          } else {
            if (result.description.indexOf('are in your cart') >= 0) {
              openAlert(allInCart);
            } else {
              openAlert(result.description);
            }
          }
        })
        .catch(error => {
          console.log('catch', error);
        });
    })
    .catch(error => {
      console.log('catch', error);
    });
}

/**
 * Adjusts a cart line's quantity and reports the change to the data layer.
 * @param {number} item_compare_at_price - Minor units, not a display string.
 * @param {number} item_price - Minor units, not a display string.
 */
function quantityCartItem(productId, minusPlus, isGift, item_title, affiliation, item_compare_at_price, item_price) {
  let currentQuantity = document.getElementById(`Quantity-${productId}`);
  if (currentQuantity || isGift) {
    let currentQuantityValue = 0;
    let eventName = 'remove_from_cart';
    let removeAll = false;
    const itemCompareAtPrice = Number(item_compare_at_price) || 0;
    const itemPrice = Number(item_price) || 0;

    const itemData = {
      id: productId,
      title: String(item_title),
      vendor: affiliation,
      item_price: itemPrice / 100,
      discount: (itemCompareAtPrice - itemPrice) / 100,
    };

    if (!isGift) {
      currentQuantityValue = Number(currentQuantity.value);
      if (minusPlus == 0) {
        eventName = 'remove_from_cart';
        removeAll = true;
        adjustQuantityDataLayerPush(itemData, eventName, currentQuantityValue, removeAll);
        currentQuantityValue = 0;
      } else if (minusPlus > 0) {
        eventName = 'add_to_cart';
        removeAll = false;
        adjustQuantityDataLayerPush(itemData, eventName, currentQuantityValue, removeAll);
        currentQuantityValue = currentQuantityValue + 1
      } else if (minusPlus < 0) {
        eventName = 'remove_from_cart';
        removeAll = false;
        adjustQuantityDataLayerPush(itemData, eventName, currentQuantityValue, removeAll);
        currentQuantityValue = currentQuantityValue - 1
      }
    }

    let addData = {
      updates: {
        [productId]: currentQuantityValue,
      },
    };

    fetch(marketUrl('cart/update.js'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(addData),
    })
      .then(response => {
        if (response.status === 200) {
          if (!isGift) {
            currentQuantity.value = currentQuantityValue;

            if (currentQuantityValue <= 0) {
              removeProductFromCart(productId);
            }
          } else {
            document.getElementById('check-gift').checked = false;
          }

          getCartTotal();
        }
      })
      .catch(error => {
        console.log('catch', error);
      });
  }
}

function removeGiftProduct(productId) {
  let addData = {
    updates: {
      [productId]: 0,
    },
  };

  fetch(marketUrl('cart/update.js'), {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
    body: JSON.stringify(addData),
  })
    .then(response => {
      if (response.status === 200) {
        // Re-read rather than writing a literal: the total must carry the cart's own currency.
        getCartTotal();
      }
    })
    .catch(error => {
      console.log('catch', error);
    });
}

function removeProductFromCart(productId) {
  document.getElementById(`cart-product-${productId}`).remove();
}

function checkTerms(elem) {
  document.getElementById('cart-checkout-button').disabled = !elem.checked;
}

/**
 * Adds or removes the gift-wrap line.
 *
 * The line must carry _isGift: getCartInfo derives this checkbox from that
 * property, so a line without it leaves the box unchecked while the gift wrap
 * sits in the cart, and the next click adds a second one instead of removing.
 * That means passing isGift in its real position rather than as a third
 * argument, hence the product's own details rather than placeholders.
 * @param {HTMLInputElement} elem - The checkbox, carrying the gift product's data attributes.
 */
function addGiftWrap(elem) {
  const variantId = Number(elem.dataset.giftVariant);
  const price = Number(elem.dataset.giftPrice) || 0;
  const title = elem.dataset.giftTitle || '';
  const vendor = elem.dataset.giftVendor || '';

  if (elem.checked) {
    addToCart(variantId, title, vendor, price, vendor, '', '', price, true);
  } else {
    quantityCartItem(variantId, 0, true, title, vendor, price, price);
  }
}

function addClassesHover() {
  document.querySelector('.header-top').classList.remove('header-video-transparent-bg');

  document.querySelectorAll('.header-top .top-menu-links').forEach(elem => elem.classList.remove('header-video-color'));
  document.querySelector('.header-top__language select').classList.remove('header-video-color');
  document.querySelectorAll('.header-top__search-cart .icons').forEach(elem => elem.classList.remove('header-video-color'));
  document.querySelectorAll('.header-top__search-cart .icons div').forEach(elem => elem.classList.remove('header-video-color'));

  document.querySelector('.header-top__search-cart select').classList.remove('header-video-transparent-bg-select');
  document.querySelector('.header-top__burger').classList.remove('header-video-color');
  document.querySelector('.header-top__burger-mobile').classList.remove('header-video-color');
}

function removeClassesHover() {
  document.querySelector('.header-top').classList.add('header-video-transparent-bg');

  document.querySelectorAll('.header-top .top-menu-links').forEach(elem => elem.classList.add('header-video-color'));
  document.querySelector('.header-top__language select').classList.add('header-video-color');
  document.querySelectorAll('.header-top__search-cart .icons').forEach(elem => elem.classList.add('header-video-color'));
  document.querySelectorAll('.header-top__search-cart .icons div').forEach(elem => elem.classList.add('header-video-color'));

  document.querySelector('.header-top__search-cart select').classList.add('header-video-transparent-bg-select');
  document.querySelector('.header-top__burger').classList.add('header-video-color');
  document.querySelector('.header-top__burger-mobile').classList.add('header-video-color');
}

function addGrayColor(element) {
  document.getElementById(element).style.setProperty('color', 'red', '!important');
}

function fetchProductList(url) {
  fetch(url)
    .then(response => response.text())
    .then(responseText => {
      const html = responseText;
      renderProductList(html);
    });
}

// render the products from the fetch response
function renderProductList(html) {
  const innerHTML = new DOMParser().parseFromString(html, 'text/html');

  let products_result = innerHTML.getElementById('products-list-grid');

  let temp = innerHTML.getElementById('pagination-page');
  const tempCurrentPage = temp.getAttribute('current-page');

  if (products_result) {
    let tempDiv = document.createElement('div');
    tempDiv.innerHTML = products_result.innerHTML;
    document.getElementById('products-list-grid').append(...products_result.childNodes);
    document.getElementById('not-products-found').style.display = 'none';
    let temp = document.getElementById('collections__list');
    if (temp) temp.style = '';
    current_page = tempCurrentPage;
  } else {
    // check if we reached the final pagination page or not products were found
    const checkEnd = innerHTML.getElementById('pagination-end');
    let checkEndText = checkEnd.innerHTML.toUpperCase();
    if (checkEndText == 'END') {
    } else {
      document.getElementById('not-products-found').style.display = 'flex';
      let temp = document.getElementById('collections__list');
      if (temp) temp.style.display = 'none';
    }
    current_page = -1;
  }


  document.querySelector('.collections__spinner').style.display = 'none';
  // set the flag to allow another request
  waitFetch = false;
}

document.addEventListener("DOMContentLoaded", function () {
  const cartCheckoutButton = document.getElementById("cart-checkout-button");
  const eventName = 'begin_checkout';

  if (cartCheckoutButton) {
    cartCheckoutButton.addEventListener("click", function (event) {
      if (!cartCheckoutButton.disabled) {
        getCartForDataLayer(eventName);
      }
    });
  }
});

function toggleVolumeIcon(id) {
  const videoElements = document.querySelectorAll(`#video-${id}`);

  videoElements.forEach(videoElement => {
    videoElement.muted = !videoElement.muted;

    if (videoElement.muted) {
      videoElement.parentElement.querySelector('.volume-on').classList.add('banner__button-muted--hide');
      videoElement.parentElement.querySelector('.volume-muted').classList.remove('banner__button-muted--hide');
    } else {
      videoElement.parentElement.querySelector('.volume-on').classList.remove('banner__button-muted--hide');
      videoElement.parentElement.querySelector('.volume-muted').classList.add('banner__button-muted--hide');
    }
  });

}